// ============================================================================
// MissingInvalidData ViewModel
// ============================================================================

import { useState, useCallback, useEffect } from "react";
import type { Job } from "../../../../../../api/types";
import type { MissingInvalidDataProps, MissingInvalidDataViewModel } from "./MissingInvalidData.types";
import { getJobDisplayName } from "../../shared";

/**
 * ViewModel hook for MissingInvalidData component
 */
export function useMissingInvalidDataViewModel(props: MissingInvalidDataProps): MissingInvalidDataViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onUpdateJob, onDeleteJob, onRegisterActions } = props;

	// State for delete confirmation dialog
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);

	// State to store the save callback from JobEditForm
	const [saveCallback, setSaveCallback] = useState<(() => void) | null>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;
	const job = currentIssue?.job ?? null;

	// Count error fields
	const errorFieldCount = currentIssue ? Object.entries(currentIssue.errors).length : 0;

	// Get job display name
	const getJobDisplayNameFn = useCallback((): string => {
		if (!job) return "this job";
		return getJobDisplayName(job);
	}, [job]);

	// Handle delete with confirmation
	const handleDeleteClick = useCallback(() => {
		setShowDeleteDialog(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (job) {
			onDeleteJob(job.id);
		}
		setShowDeleteDialog(false);
	}, [job, onDeleteJob]);

	const handleCancelDelete = useCallback(() => {
		setShowDeleteDialog(false);
	}, []);

	// Handle save from the form - just update the job
	// When all validation errors are fixed, the issue will disappear from the list
	const handleSaveAndValidate = useCallback(
		(updatedJob: Job, _hasErrors: boolean) => {
			onUpdateJob(updatedJob);
		},
		[onUpdateJob],
	);

	// Register save callback from JobEditForm
	const handleRegisterSave = useCallback((saveFn: () => void) => {
		setSaveCallback(() => saveFn);
	}, []);

	// Request save (triggered from bottom bar)
	const handleRequestSave = useCallback(() => {
		if (saveCallback) {
			saveCallback();
		}
	}, [saveCallback]);

	// Register actions with parent when ready
	useEffect(() => {
		if (onRegisterActions) {
			onRegisterActions({
				requestDelete: handleDeleteClick,
				requestSave: handleRequestSave,
			});
		}
	}, [onRegisterActions, handleDeleteClick, handleRequestSave]);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		job,
		errorFieldCount,
		showDeleteDialog,
		getJobDisplayName: getJobDisplayNameFn,
		handleDeleteClick,
		handleConfirmDelete,
		handleCancelDelete,
		handleSaveAndValidate,
		handleRegisterSave,
	};
}
